import type { FC } from 'react'
import { useState } from 'react'

import CommentsLabel from '@/components/CommentsLabel'
import CommentsSidebar from '@/components/CommentsSidebar'
import { ActionButtonContainer } from '@/components/DecisionPanelCards/Shared'
import ExpandableCard from '@/components/ExpandableCard'
import OutcomeStatusPill from '@/components/OutcomeStatusPill'
import { OutcomeSectionName } from '@/lib/apollo/model/outcome'
import type { Applicant } from '@/lib/apollo/query/get-applicant'
import type { Application } from '@/types/Activity'
import type { Touchpoint } from '@/types/Touchpoints'

import CreditBody from './CreditBody'

interface Props {
  application: Application
  applicant: Applicant
  subjectTouchpoints: Touchpoint[]
  isLoadingTouchpoints: boolean
  fetchSubjectTouchpoints: () => Promise<void>
  isRestrictedReference: boolean
}

const CreditCard: FC<Props> = ({
  application,
  applicant,
  isLoadingTouchpoints,
  subjectTouchpoints,
  fetchSubjectTouchpoints,
  isRestrictedReference,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [commentsSidebarOpen, setCommentsSidebarOpen] = useState<boolean>(false)

  const outcomeSections = application?.outcome?.sections ?? []
  const creditOutcome = outcomeSections.find(
    outcomeSection => outcomeSection.name === OutcomeSectionName.credit,
  )
  const overallOutcomeStatus = application.outcome?.status
  const creditOutcomeStatus = creditOutcome?.status

  const sectionComments = subjectTouchpoints.filter(
    ({ category }) => category.toLowerCase() === 'credit',
  )

  return (
    <>
      <CommentsSidebar
        isOpen={commentsSidebarOpen}
        application={application}
        onClose={() => setCommentsSidebarOpen(false)}
        touchpoints={sectionComments}
        activeEntity="CREDIT"
        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
      />
      <ExpandableCard
        handleExpand={() => setIsExpanded(isExpanded => !isExpanded)}
        label="Credit Check"
        icon="credit"
        expandedContent={
          <CreditBody
            application={application}
            applicant={applicant}
            isRestrictedReference={isRestrictedReference}
          />
        }
        isExpanded={isExpanded}
        actionButton={
          <ActionButtonContainer>
            <div>
              <CommentsLabel
                isLoadingTouchpoints={isLoadingTouchpoints}
                onToggle={() => setCommentsSidebarOpen(!commentsSidebarOpen)}
                sectionComments={sectionComments}
              />
            </div>

            {overallOutcomeStatus && creditOutcomeStatus && (
              <OutcomeStatusPill
                overallOutcomeStatus={overallOutcomeStatus}
                outcomeStatus={creditOutcomeStatus}
              />
            )}
          </ActionButtonContainer>
        }
      />
    </>
  )
}

export default CreditCard
